import { render as toPlainText } from 'datocms-structured-text-to-plain-text';
import {
  Document as StructuredTextDocument,
  Heading,
  isHeading,
  Node,
  Record as DatoRecord,
  TypesafeStructuredText,
} from 'datocms-structured-text-utils';

import { Props } from 'components/@structured/StructuredContent/types';

export const isNodeEmpty = (node: Node) => {
  if (
    node.type === 'paragraph' &&
    node.children[0].type === 'span' &&
    node.children[0].value === ''
  )
    return true;

  return false;
};

type StructuredContentText = Props['content'];

export const isStructuredTextPresent = (
  structuredText?: StructuredContentText | TypesafeStructuredText | null,
): structuredText is StructuredContentText =>
  !isStructuredTextEmpty(structuredText);

const isStructuredTextEmpty = (
  structuredText?: StructuredContentText | TypesafeStructuredText | null,
): boolean => {
  if (!structuredText) return true;
  if ('value' in structuredText) {
    if (!structuredText.value) return true;
    if (isNodeEmpty(structuredText.value.document.children[0])) return true;
  } else {
    if (isNodeEmpty(structuredText.document.children[0])) return true;
  }

  return false;
};

const extractTextFromHeading = (
  node: Array<Node>,
  text: Array<string> = [],
) => {
  node.forEach((child) => {
    if ('children' in child) {
      extractTextFromHeading(child.children, text);
    }
    if ('value' in child) {
      text.push(child.value);
    }
  });
  return text.join('');
};

const extractHeadingsFromChildren = (
  children: Array<Node>,
  headings: Array<ExtractedStructuredTextHeading> = [],
): Array<ExtractedStructuredTextHeading> => {
  children.flat().forEach((child) => {
    if ('children' in child) {
      extractHeadingsFromChildren(child.children as Array<Node>, headings);
    }
    if (isHeading(child) && child.level <= 2) {
      headings.push({
        id: extractAnchorFromHeading(child),
        level: child.level,
        tag: `h${child.level}`,
        text: extractTextFromHeading(child.children),
      });
    }
  });
  return headings;
};

export const extractHeadings = <T extends DatoRecord>(
  structuredText?: TypesafeStructuredText<T> | StructuredTextDocument,
) => {
  if (!structuredText) return [];
  if (
    'value' in structuredText &&
    'children' in structuredText.value.document
  ) {
    return extractHeadingsFromChildren(structuredText.value.document.children);
  }
  return [];
};

export const extractAnchorFromHeading = (heading: Heading) => {
  return toPlainText(heading)!.toLowerCase().replace(/ /g, '-');
};
export type ExtractedStructuredTextHeading = {
  id: string;
  level: number;
  tag: string;
  text?: string;
};
